import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import StarRating from './StarRating';
import { CreateReviewRequest, UpdateReviewRequest, ReviewTargetTypeValue, Review } from '../../types/review';
import reviewService from '../../services/reviewService';
import { useNotification } from '../../hooks/useNotification';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  animation: ${fadeIn} 0.3s ease;
`;

const ModalContainer = styled.div`
  background: rgba(26, 35, 46, 0.95);
  backdrop-filter: blur(20px);
  border: 1px solid #00d4ff;
  border-radius: 12px;
  width: 100%;
  max-width: 420px;
  max-height: 85vh;
  overflow-y: auto;
  box-shadow: 0 20px 40px rgba(0, 212, 255, 0.3);
  position: relative;
  animation: ${fadeIn} 0.3s ease;
  margin: 10px;
`;

const ModalHeader = styled.div`
  padding: 14px 16px;
  border-bottom: 1px solid rgba(0, 212, 255, 0.2);
  position: relative;
  background: linear-gradient(135deg, rgba(0, 212, 255, 0.1), rgba(0, 153, 204, 0.05));
  border-radius: 12px 12px 0 0;
`;

const ModalTitle = styled.h2`
  color: #00d4ff;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  font-size: 16px;
  font-weight: 600;
  margin: 0;
  text-transform: uppercase;
  text-align: center;
  letter-spacing: 0.5px;
`;

const CloseButton = styled.button`
  position: absolute;
  right: 12px;
  top: 10px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: #ffffff;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  cursor: pointer;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  font-size: 16px;
  font-weight: 400;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: #00d4ff;
    color: #00d4ff;
    transform: scale(1.1);
  }
`;

const ModalBody = styled.div`
  padding: 16px;
`;

const FormGroup = styled.div`
  margin-bottom: 14px;
`;

const Label = styled.label`
  display: block;
  color: #00d4ff;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 80px;
  padding: 10px 12px;
  background: rgba(26, 35, 46, 0.6);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 8px;
  color: #ffffff;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  font-size: 13px;
  line-height: 1.4;
  resize: vertical;
  max-height: 120px;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: #00d4ff;
    box-shadow: 0 0 20px rgba(0, 212, 255, 0.2);
    background: var(--legacy-card-bg);
  }

  &::placeholder {
    color: #a0a9b8;
  }
`;

const CharCounter = styled.div`
  color: #a0a9b8;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  font-size: 11px;
  text-align: right;
  margin-top: 4px;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 16px;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 10px 18px;
  background: ${props => props.variant === 'secondary' ? 'rgba(255, 255, 255, 0.05)' : 'linear-gradient(135deg, #00d4ff, #0099cc)'};
  border: 1px solid ${props => props.variant === 'secondary' ? 'rgba(255, 255, 255, 0.2)' : '#00d4ff'};
  color: ${props => props.variant === 'secondary' ? '#ffffff' : '#ffffff'};
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.3s ease;

  &:hover {
    background: ${props => props.variant === 'secondary' ? 'rgba(255, 255, 255, 0.1)' : 'linear-gradient(135deg, #00a8cc, #007799)'};
    transform: translateY(-2px);
    box-shadow: ${props => props.variant === 'secondary' ? '0 4px 15px rgba(255, 255, 255, 0.1)' : '0 4px 15px rgba(0, 212, 255, 0.3)'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const ErrorMessage = styled.div`
  color: #ff6b6b;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  font-size: 12px;
  margin-top: 8px;
  padding: 10px;
  background: rgba(255, 107, 107, 0.1);
  border: 1px solid rgba(255, 107, 107, 0.3);
  border-radius: 6px;
`;

const ExistingReviewNote = styled.div`
  background: rgba(255, 193, 7, 0.1);
  border: 1px solid rgba(255, 193, 7, 0.3);
  border-radius: 8px;
  padding: 10px;
  color: #ffc107;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  font-size: 12px;
  margin-bottom: 14px;
  text-align: center;
  line-height: 1.4;
`;

const MediaSection = styled.div`
  margin-bottom: 14px;
`;

const PhotoUploadContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
`;

const PhotoPreview = styled.div`
  position: relative;
  width: 56px;
  height: 56px;
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid rgba(0, 212, 255, 0.3);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const RemovePhotoButton = styled.button`
  position: absolute;
  top: 2px;
  right: 2px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: rgba(255, 107, 107, 0.9);
  border: none;
  color: white;
  font-size: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: #ff6b6b;
    transform: scale(1.1);
  }
`;

const AddPhotoButton = styled.label`
  width: 56px;
  height: 56px;
  border: 2px dashed rgba(0, 212, 255, 0.3);
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  color: #a0a9b8;
  font-size: 10px;
  gap: 2px;

  &:hover {
    border-color: #00d4ff;
    color: #00d4ff;
    background: rgba(0, 212, 255, 0.05);
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const HiddenInput = styled.input`
  display: none;
`;

const PhotoCount = styled.span`
  color: #a0a9b8;
  font-size: 11px;
  margin-left: 6px;
  font-weight: 400;
`;

const YouTubeInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  background: rgba(26, 35, 46, 0.6);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 8px;
  color: #ffffff;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  font-size: 13px;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: #00d4ff;
    box-shadow: 0 0 20px rgba(0, 212, 255, 0.2);
    background: var(--legacy-card-bg);
  }

  &::placeholder {
    color: #a0a9b8;
  }
`;

const YouTubeHint = styled.div`
  color: #a0a9b8;
  font-size: 10px;
  margin-top: 4px;
`;

const UploadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #00d4ff;
  font-size: 8px;
`;

interface ReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  targetType: ReviewTargetTypeValue;
  targetId: string;
  targetName: string;
  onReviewSubmitted?: () => void;
  existingReview?: Review | null;
}

const MAX_PHOTOS = 5;

// Image type for managing uploads
interface ImageItem {
  id: string;
  url: string;
  file?: File;
  isUploading?: boolean;
  isExisting?: boolean;
}

const ReviewModal: React.FC<ReviewModalProps> = ({
  isOpen,
  onClose,
  targetType,
  targetId,
  targetName,
  onReviewSubmitted,
  existingReview
}) => {
  const [rating, setRating] = useState(existingReview?.rating || 5);
  const [reviewText, setReviewText] = useState(existingReview?.reviewText || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { showSuccess, showError } = useNotification();

  // Photo and video state
  const [images, setImages] = useState<ImageItem[]>([]);
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [isUploadingImages, setIsUploadingImages] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const maxChars = 1000;
  const isEditing = Boolean(existingReview);

  useEffect(() => {
    if (isOpen) {
      setRating(existingReview?.rating || 5);
      setReviewText(existingReview?.reviewText || '');
      setError(null);

      // Load existing images and YouTube URL if editing
      if (existingReview) {
        const existingImages: ImageItem[] = (existingReview.images || []).map((url, index) => ({
          id: `existing-${index}`,
          url,
          isExisting: true
        }));
        setImages(existingImages);
        setYoutubeUrl(existingReview.youtubeVideoUrl || '');
      } else {
        setImages([]);
        setYoutubeUrl('');
      }
    }
  }, [isOpen, existingReview]);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const remainingSlots = MAX_PHOTOS - images.length;
    if (remainingSlots <= 0) {
      showError('Limit reached', `Maximum ${MAX_PHOTOS} photos allowed`);
      return;
    }

    const filesToAdd = Array.from(files).slice(0, remainingSlots);

    // Create preview items
    const newImages: ImageItem[] = filesToAdd.map((file, index) => ({
      id: `new-${Date.now()}-${index}`,
      url: URL.createObjectURL(file),
      file,
      isUploading: false,
      isExisting: false
    }));

    setImages(prev => [...prev, ...newImages]);

    // Clear input for re-selection
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveImage = (imageId: string) => {
    setImages(prev => {
      const image = prev.find(img => img.id === imageId);
      // Revoke blob URL if it's a local file
      if (image && !image.isExisting && image.url.startsWith('blob:')) {
        URL.revokeObjectURL(image.url);
      }
      return prev.filter(img => img.id !== imageId);
    });
  };

  const validateYouTubeUrl = (url: string): boolean => {
    if (!url) return true; // Empty is valid
    const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com\/(watch\?v=|embed\/|v\/)|youtu\.be\/)[\w-]+/;
    return youtubeRegex.test(url);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    // Validate YouTube URL
    if (youtubeUrl && !validateYouTubeUrl(youtubeUrl)) {
      setError('Invalid YouTube URL format');
      return;
    }

    setError(null);
    setIsSubmitting(true);

    try {
      // Step 1: Upload new images to blob storage
      let uploadedImageUrls: string[] = [];
      const existingImageUrls = images.filter(img => img.isExisting).map(img => img.url);
      const newImages = images.filter(img => !img.isExisting && img.file);

      if (newImages.length > 0) {
        setIsUploadingImages(true);
        try {
          const files = newImages.map(img => img.file!);
          uploadedImageUrls = await reviewService.uploadImages(files);
        } catch (uploadErr: any) {
          setError('Failed to upload images: ' + (uploadErr.message || 'Unknown error'));
          setIsSubmitting(false);
          setIsUploadingImages(false);
          return;
        }
        setIsUploadingImages(false);
      }

      // Combine existing and newly uploaded image URLs
      const allImageUrls = [...existingImageUrls, ...uploadedImageUrls];

      const request: CreateReviewRequest | UpdateReviewRequest = {
        rating,
        reviewText: reviewText.trim(),
        images: allImageUrls,
        youtubeVideoUrl: youtubeUrl.trim()
      };

      if (isEditing && existingReview) {
        // Update existing review
        switch (targetType) {
          case 'field':
            await reviewService.updateFieldReview(existingReview.id, request as UpdateReviewRequest);
            break;
          case 'referee':
            await reviewService.updateRefereeReview(existingReview.id, request as UpdateReviewRequest);
            break;
          case 'mission':
            await reviewService.updateMissionReview(existingReview.id, request as UpdateReviewRequest);
            break;
          case 'game':
            await reviewService.updateGameReview(existingReview.id, request as UpdateReviewRequest);
            break;
          case 'map':
            await reviewService.updateMapReview(existingReview.id, request as UpdateReviewRequest);
            break;
        }
        showSuccess('Review updated', 'Review updated successfully!');
      } else {
        // Create new review
        const createRequest: CreateReviewRequest = {
          ...request as UpdateReviewRequest,
          targetType,
          targetId
        };

        switch (targetType) {
          case 'field':
            await reviewService.createFieldReview(targetId, createRequest);
            break;
          case 'referee':
            await reviewService.createRefereeReview(targetId, createRequest);
            break;
          case 'mission':
            await reviewService.createMissionReview(targetId, createRequest);
            showSuccess('Review submitted', 'Review submitted for moderation!');
            break;
          case 'game':
            await reviewService.createGameReview(targetId, createRequest);
            break;
          case 'map':
            await reviewService.createMapReview(targetId, createRequest);
            break;
        }

        if (targetType !== 'mission') {
          showSuccess('Review submitted', 'Review submitted successfully!');
        }
      }

      if (onReviewSubmitted) {
        onReviewSubmitted();
      }
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to submit review');
      showError('Submission failed', 'Failed to submit review');
    } finally {
      setIsSubmitting(false);
      setIsUploadingImages(false);
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <Overlay onClick={handleOverlayClick}>
      <ModalContainer>
        <ModalHeader>
          <ModalTitle>
            {isEditing ? 'Edit Review' : 'Write Review'} - {targetName}
          </ModalTitle>
          <CloseButton onClick={onClose}>×</CloseButton>
        </ModalHeader>

        <ModalBody>
          {isEditing && (
            <ExistingReviewNote>
              You are editing your existing review. Changes will update your current rating.
            </ExistingReviewNote>
          )}

          <form onSubmit={handleSubmit}>
            <FormGroup>
              <Label>Rating *</Label>
              <StarRating
                rating={rating}
                readOnly={false}
                size="large"
                showRating={true}
                onRatingChange={setRating}
              />
            </FormGroup>

            <FormGroup>
              <Label>Review (Optional)</Label>
              <TextArea
                value={reviewText}
                onChange={(e) => setReviewText(e.target.value)}
                placeholder="Share your experience..."
                maxLength={maxChars}
              />
              <CharCounter>
                {reviewText.length}/{maxChars}
              </CharCounter>
            </FormGroup>

            {/* Photo upload section */}
            <MediaSection>
              <Label>
                Photos
                <PhotoCount>({images.length}/{MAX_PHOTOS})</PhotoCount>
              </Label>
              <PhotoUploadContainer>
                {images.map((image) => (
                  <PhotoPreview key={image.id}>
                    <img src={image.url} alt="Preview" />
                    <RemovePhotoButton
                      type="button"
                      onClick={() => handleRemoveImage(image.id)}
                      title="Remove photo"
                    >
                      ×
                    </RemovePhotoButton>
                    {image.isUploading && (
                      <UploadingOverlay>...</UploadingOverlay>
                    )}
                  </PhotoPreview>
                ))}
                {images.length < MAX_PHOTOS && (
                  <AddPhotoButton>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 5v14M5 12h14" />
                    </svg>
                    <span>Add</span>
                    <HiddenInput
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleFileSelect}
                    />
                  </AddPhotoButton>
                )}
              </PhotoUploadContainer>
            </MediaSection>

            {/* YouTube URL field */}
            <FormGroup>
              <Label>YouTube Video (Optional)</Label>
              <YouTubeInput
                type="text"
                value={youtubeUrl}
                onChange={(e) => setYoutubeUrl(e.target.value)}
                placeholder="https://www.youtube.com/watch?v=..."
              />
              <YouTubeHint>
                Paste a YouTube video link
              </YouTubeHint>
            </FormGroup>

            {error && <ErrorMessage>{error}</ErrorMessage>}

            <ButtonContainer>
              <Button type="button" variant="secondary" onClick={onClose}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting || rating === 0}
              >
                {isUploadingImages
                  ? 'Uploading photos...'
                  : isSubmitting
                    ? 'Submitting...'
                    : isEditing
                      ? 'Update Review'
                      : 'Submit Review'}
              </Button>
            </ButtonContainer>
          </form>
        </ModalBody>
      </ModalContainer>
    </Overlay>
  );
};

export default ReviewModal;